import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Download, ChevronDown } from "lucide-react";

const BLUEPRINT_OVERLAY =
  "https://images.unsplash.com/photo-1721244654392-9c912a6eb236";

// Local hero video — your original Tirupati clip, served from /public.
// WebM/VP9 first (best codec for modern browsers); H.264 MP4 second (universal incl. Safari/iOS).
const HERO_VIDEO_WEBM = "/hero.webm?v=9";
const HERO_VIDEO = "/hero.mp4?v=9";

const STATS = [
  { value: 10, suffix: "+", label: "Years of Excellence" },
  { value: 25, suffix: "+", label: "Yrs Leadership Experience" },
  { value: 100, suffix: "%", label: "Quality Commitment" },
];

const CERTS = ["ISO 9001:2015", "IATF 16949:2016", "VDA 6.3"];

function Counter({ value, suffix = "", duration = 1800 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(Math.round(eased * value));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className="num-display">
      {n}
      {suffix}
    </span>
  );
}

export default function Hero() {
  const videoRef = useRef(null);

  // Robust autoplay. Browsers block muted-autoplay in some scenarios; we retry
  // on every meaningful event AND on the FIRST user interaction (scroll/touch/click)
  // anywhere on the page. Once user has interacted, autoplay policy lifts.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // React sometimes doesn't apply muted to the underlying element
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");

    let cancelled = false;
    const tryPlay = () => {
      if (cancelled) return;
      if (!v.paused) return;
      v.muted = true; // belt-and-suspenders
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // ignore — will retry on next event
        });
      }
    };

    // Initial attempt + on every meaningful media event
    tryPlay();
    v.addEventListener("loadedmetadata", tryPlay);
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    v.addEventListener("canplaythrough", tryPlay);

    // CRITICAL: whenever the browser pauses the video for ANY reason
    // (tab switch, low-power mode, network stall, etc), immediately resume.
    const onPause = () => {
      // Microtask delay so internal state settles before we re-issue play()
      setTimeout(tryPlay, 50);
    };
    v.addEventListener("pause", onPause);
    v.addEventListener("stalled", tryPlay);
    v.addEventListener("waiting", tryPlay);
    v.addEventListener("ended", tryPlay); // loop attr should handle but be safe
    v.addEventListener("emptied", tryPlay);

    document.addEventListener("visibilitychange", tryPlay);
    window.addEventListener("focus", tryPlay);
    window.addEventListener("pageshow", tryPlay);

    // Unblock on first user interaction (lifts strict autoplay policies)
    const unblock = () => {
      tryPlay();
      window.removeEventListener("scroll", unblock);
      window.removeEventListener("click", unblock);
      window.removeEventListener("touchstart", unblock);
      window.removeEventListener("keydown", unblock);
      window.removeEventListener("mousemove", unblock);
    };
    window.addEventListener("scroll", unblock, { passive: true });
    window.addEventListener("click", unblock, { passive: true });
    window.addEventListener("touchstart", unblock, { passive: true });
    window.addEventListener("keydown", unblock);
    window.addEventListener("mousemove", unblock, { passive: true });

    // Permanent watchdog — every 1.5 s, if the video is paused, restart it.
    // No timeout, no end — runs the entire lifetime of the page.
    const watchdog = setInterval(tryPlay, 1500);

    return () => {
      cancelled = true;
      clearInterval(watchdog);
      v.removeEventListener("loadedmetadata", tryPlay);
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
      v.removeEventListener("canplaythrough", tryPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("stalled", tryPlay);
      v.removeEventListener("waiting", tryPlay);
      v.removeEventListener("ended", tryPlay);
      v.removeEventListener("emptied", tryPlay);
      document.removeEventListener("visibilitychange", tryPlay);
      window.removeEventListener("focus", tryPlay);
      window.removeEventListener("pageshow", tryPlay);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden isolate"
    >
      {/* Layer 1: Branded navy background — shown only while the video buffers */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-40"
        style={{
          background:
            "radial-gradient(120% 80% at 20% 0%, #14406F 0%, #0B2E59 45%, #07203F 100%)",
        }}
      />
      <div className="absolute inset-0 -z-30">
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      </div>

      {/* Layer 2: Floating CAD/blueprint graphic — subtle */}
      <div
        className="absolute inset-0 -z-20 opacity-[0.08] mix-blend-screen"
        style={{
          backgroundImage: `url(${BLUEPRINT_OVERLAY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden
      />

      {/* Layer 3: Navy gradient overlay — kept light so the video shows through */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(120deg, rgba(11,46,89,0.55) 0%, rgba(17,74,139,0.32) 45%, rgba(11,46,89,0.18) 100%)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 -z-10 bp-grid opacity-25" aria-hidden />

      {/* Layer 4: Animated global trade route lines */}
      <svg
        className="absolute inset-0 -z-10 w-full h-full text-brand-orange/60"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <radialGradient id="hub" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F5A623" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F5A623" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g fill="none" stroke="currentColor" strokeWidth="1.2">
          <path className="route-line" d="M 220 620 Q 520 200 880 320" />
          <path className="route-line" d="M 220 620 Q 700 760 1240 540" />
          <path className="route-line" d="M 220 620 Q 420 420 1140 220" />
          <path className="route-line" d="M 220 620 Q 320 380 720 180" />
        </g>
        <circle cx="220" cy="620" r="40" fill="url(#hub)" />
        <circle cx="220" cy="620" r="4" fill="#F5A623" />
        {[
          [880, 320],
          [1240, 540],
          [1140, 220],
          [720, 180],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3.5" fill="#52B54B" />
        ))}
      </svg>

      {/* Content */}
      <div className="relative container-x pt-44 pb-28 lg:pt-48 lg:pb-32 min-h-[100svh] flex items-center">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 w-full items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 text-white"
          >
            <div className="inline-flex items-center gap-3 text-white/90 mb-7">
              <span className="divider-rule" />
              <span className="eyebrow !text-white/80">
                Engineering Sourcing · Manufacturing · Global Supply
              </span>
            </div>

            <h1 className="h-hero">
              Connecting Markets.
              <br />
              <span className="text-brand-orange">Delivering Solutions.</span>
            </h1>

            <p className="mt-7 text-white/85 text-lg max-w-2xl leading-relaxed">
              Tirupati Global Sourcing Company (TGSC) is an India-based
              engineering sourcing and manufacturing organisation specialising
              in precision-engineered, multi-commodity components and
              integrated supply chain solutions for OEMs worldwide — backed by
              the Tirupati Group of Companies, a diversified manufacturing
              group with advanced production facilities in India.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                data-testid="hero-cta-rfq"
                href="#rfq"
                className="btn-accent"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </a>
              <a
                data-testid="hero-cta-download"
                href="#about"
                className="btn-ghost-light"
              >
                <Download className="h-4 w-4" /> Download Profile
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.22em] text-white/65">
              {CERTS.map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-brand-green inline-block" />
                  {c}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Floating glassmorphism stat panel */}
          <motion.aside
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="lg:col-span-5"
            data-testid="hero-stats-panel"
          >
            <div className="glass-dark p-7 lg:p-8 relative overflow-hidden">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand-orange/20 blur-3xl" />
              <div className="absolute -left-10 -bottom-10 h-44 w-44 rounded-full bg-brand-earth/20 blur-3xl" />

              <div className="flex items-center justify-between mb-6 relative">
                <div className="eyebrow !text-white/80">At a glance</div>
                <div className="h-px flex-1 mx-4 bg-white/15" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-brand-orange">
                  Tirupati
                </span>
              </div>

              <div className="grid grid-cols-3 gap-5 relative">
                {STATS.map((s) => (
                  <div key={s.label} className="text-left">
                    <Counter value={s.value} suffix={s.suffix} />
                    <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/70 leading-snug">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="h-px bg-white/15 my-7" />

              <div className="space-y-3 relative">
                {CERTS.map((c) => (
                  <div
                    key={c}
                    className="flex items-center justify-between text-white/90"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-2 w-2 bg-brand-green inline-block" />
                      <span className="font-semibold text-sm">{c}</span>
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                      Certified
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                  Headquartered in Pune, India
                </div>
                <div className="flex gap-1.5">
                  {["#52B54B", "#1E88E5", "#F5A623"].map((c) => (
                    <span
                      key={c}
                      className="h-1.5 w-6 inline-block"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#global-reach"
        data-testid="scroll-indicator"
        className="absolute left-1/2 -translate-x-1/2 bottom-7 text-white/80 hover:text-white flex flex-col items-center gap-2 z-10"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ChevronDown className="h-5 w-5 scroll-dot" />
      </a>
    </section>
  );
}
